import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "styled-components";

import * as serviceWorker from "./serviceWorker";
import store from "./store";
import theme from "./theme";
import urls from "./urls";
import DefaultLayout from "./ui/pages/DefaultLayout";
import NotFound from "./ui/pages/NotFound";
import Profile from "./ui/pages/Profile";
import SignIn from "./ui/pages/SignIn";
import ContactPage from "./ui/ContactPage";
import Frontpage from "./ui/Frontpage";
import "./util/polyfills";

// Let's not import the .scss file because the image snapshot tests use the file
// If we used scss here, the tests would not be out of sync with the code.
import "./styles.scss";

// Lazy load major components
const ChordProgressionDrill = React.lazy(
  () =>
    import(
      /* webpackChunkName: "drills" */ "./drills/chord-progression/ChordProgressionDrill"
    )
);
const ContextualDrill = React.lazy(
  () =>
    import(
      /* webpackChunkName: "drills" */ "./drills/contextual/ContextualDrill"
    )
);

const ChordConstructionDrill = React.lazy(
  () =>
    import(
      /* webpackChunkName: "drills" */ "./drills/chord-construction/ChordConstructionDrill"
    )
);

const IntervalTrainingDrill = React.lazy(
  () =>
    import(
      /* webpackChunkName: "drills" */ "./drills/interval-training/IntervalTrainingDrill"
    )
);

const FretboardIntervalDrill = React.lazy(
  () =>
    import(
      /* webpackChunkName: "drills" */ "./drills/fretboard-interval/FretboardIntervalDrill"
    )
);

const FretboardNotesDrill = React.lazy(
  () =>
    import(
      /* webpackChunkName: "drills" */ "./drills/fretboard-notes/FretboardNotesDrill"
    )
);

const MelodicDictationDrill = React.lazy(
  () =>
    import(
      /* webpackChunkName: "drills" */ "./drills/melodic-dictation/MelodicDictationDrill"
    )
);

/**
 * Remove the loading animation from DOM. The animation is defined in
 * `public/index.html`
 */
const AnimationRemover = () => {
  useEffect(() => {
    if (typeof document !== undefined) {
      const element = document.getElementById("loading");
      if (element) {
        element.outerHTML = "";
      }
    }
  }, []);

  return null;
};

const root = createRoot(document.getElementById("musicdrill-root")!);
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <AnimationRemover />
        <React.Suspense fallback={null}>
          <BrowserRouter>
            <Routes>
              <Route
                path={urls.frontpage}
                element={
                  <DefaultLayout title="">
                    <Frontpage />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.chordProgressionDrill}
                element={
                  <DefaultLayout title="Chord Progression Ear Training">
                    <ChordProgressionDrill />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.contextual}
                element={
                  <DefaultLayout title="Contextual Ear Training">
                    <ContextualDrill />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.melodicDictation}
                element={
                  <DefaultLayout title="Melodic Dictation Ear Training">
                    <MelodicDictationDrill />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.chordConstruction}
                element={
                  <DefaultLayout title="Chord Construction Training">
                    <ChordConstructionDrill />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.intervalTraining}
                element={
                  <DefaultLayout title="Interval Training">
                    <IntervalTrainingDrill />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.fretboardNotes}
                element={
                  <DefaultLayout title="Guitar Fretboard Note Training">
                    <FretboardNotesDrill />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.fretboardInterval}
                element={
                  <DefaultLayout title="Guitar Fretboard Interval Training">
                    <FretboardIntervalDrill />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.profile}
                element={
                  <DefaultLayout title="Profile">
                    {/* @ts-expect-error Props provided by Redux connect */}
                    <Profile />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.signIn}
                element={
                  <DefaultLayout title="Sign in">
                    <SignIn />
                  </DefaultLayout>
                }
              />
              <Route
                path={urls.contact}
                element={
                  <DefaultLayout title="Contact">
                    <ContactPage />
                  </DefaultLayout>
                }
              />
              <Route
                path="*"
                element={
                  <DefaultLayout title="Page Not Found">
                    <NotFound />
                  </DefaultLayout>
                }
              />
            </Routes>
          </BrowserRouter>
        </React.Suspense>
      </ThemeProvider>
    </Provider>
  </React.StrictMode>
);

serviceWorker.register();
